import api from "@/lib/api";

// Session-level media cache: each file is fetched once, then served instantly
// from an in-memory object URL (works even when a proxy disables HTTP caching).
const cache = new Map();     // key -> objectURL
const inflight = new Map();  // key -> Promise<objectURL>

const keyFor = (fileId, thumb) => `${thumb ? "t" : "f"}:${fileId}`;

export function getCached(fileId, thumb = false) {
  return cache.get(keyFor(fileId, thumb));
}

// When the current user sends an image, show it instantly from the local file
// (no download round-trip) by seeding both thumb + full cache entries.
export function seedLocal(fileId, objectUrl) {
  cache.set(keyFor(fileId, true), objectUrl);
  cache.set(keyFor(fileId, false), objectUrl);
}

export async function fetchMedia(fileId, { thumb = false, onProgress } = {}) {
  const key = keyFor(fileId, thumb);
  if (cache.has(key)) return cache.get(key);
  if (inflight.has(key)) return inflight.get(key);

  const endpoint = thumb ? `/files/${fileId}/thumb` : `/files/${fileId}/download`;
  const p = api
    .get(endpoint, { responseType: "blob", onDownloadProgress: onProgress })
    .then((res) => {
      const url = URL.createObjectURL(res.data);
      cache.set(key, url);
      inflight.delete(key);
      return url;
    })
    .catch((e) => {
      inflight.delete(key);
      throw e;
    });
  inflight.set(key, p);
  return p;
}

// Native browser download/open via a short-lived signed ticket. The browser
// streams straight to disk (no in-memory blob), which is reliable on iOS and
// works for very large files.
function isStandalonePWA() {
  return (
    window.navigator.standalone === true ||
    window.matchMedia?.("(display-mode: standalone)").matches === true
  );
}

export async function nativeDownload(fileId, { inline = false } = {}) {
  const { data } = await api.post(`/files/${fileId}/download-ticket`, null, {
    params: { disposition: inline ? "inline" : "attachment" },
  });
  const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;

  // Installed iOS/Android PWA: window.open() after an await is treated as a
  // blocked popup (loses the user-gesture), so navigate the top window instead.
  if (isStandalonePWA()) {
    window.location.href = url;
    return;
  }

  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener";
  if (!inline) a.download = "";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
